<template>
    <!-- eslint-disable max-len -->
    <section class="max-w-7xl mx-auto px-5">
        <div class="flex flex-col md:items-start md:flex-row rounded-3xl p-10 my-20 bg-slate-800/60 ring-1 ring-slate-300/10">
            <div class="md:mr-10">
                <a :href="comic.path"
                   class="inline-block shadow relative group"
                   data-lity>
                    <div class="absolute -inset-[2px] border-2 border-transparent opacity-0 [background:linear-gradient(var(--color-slate-800),var(--color-slate-800))_padding-box,linear-gradient(to_top,var(--color-indigo-400),var(--color-cyan-400),var(--color-sky-500))_border-box] group-hover:opacity-100"></div>
                    <div class="relative">
                        <img class="w-72"
                             :src="comic.thumbnail"
                             :alt="comic.title">
                    </div>
                </a>
            </div>
            <div class="flex flex-col pt-10 md:pt-0">
                <h2 class="text-2xl bg-linear-to-r from-indigo-200 via-sky-400 to-indigo-200 bg-clip-text text-transparent xl:text-3xl mb-1">
                    {{ comic.title }}
                </h2>
                <h5 class="text-white text-lg xl:text-xl mb-10">
                    {{ comic.subtitle }}
                </h5>
                <!-- eslint-disable max-len -->
                <div class="prose"
                     :class="{'mb-5': comic.description}"
                     v-html="comic.description"></div>
                <div class="flex flex-col space-y-5 lg:flex-row lg:space-y-0 lg:space-x-5">
                    <template v-if="comic.hasRender || comic.hasPictureRender">
                        <div v-if="comic.hasRender"
                             class="flex items-center">
                            <a class="btn-sky font-semibold flex items-center"
                               :class="{'rounded-r-none': comic.renderLanguages.length}"
                               :href="route('reader', readerLanguage ? [app.locale, comic.id, readerLanguage.code] : [app.locale, comic.id])"
                               target="_blank">
                                <span class="mr-2">
                                    <book-open-icon class="h-5 w-5" />
                                </span>
                                {{ trans['messages.open_reader'] }}
                            </a>
                            <HeadlessMenu v-if="comic.renderLanguages.length"
                                          as="div"
                                          class="relative">
                                <div>
                                    <MenuButton class="btn-sky font-semibold bg-sky-400 hover:bg-sky-500 rounded-l-none uppercase flex items-center">
                                        {{ readerLanguage.code }}
                                        <ChevronDownIcon class="h-5 w-5" />
                                    </MenuButton>
                                </div>
                                <transition enter-active-class="transition ease-out duration-100"
                                            enter-from-class="transform opacity-0 scale-95"
                                            enter-to-class="transform opacity-100 scale-100"
                                            leave-active-class="transition ease-in duration-75"
                                            leave-from-class="transform opacity-100 scale-100"
                                            leave-to-class="transform opacity-0 scale-95">
                                    <MenuItems class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-lg bg-slate-800 py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                                        <MenuItem v-for="language in comic.renderLanguages"
                                                  v-slot="{ active }"
                                                  :key="language.id">
                                            <button :class="[active ? 'bg-slate-700' : '', 'flex items-center w-full px-4 py-2 text-sm text-slate-400']"
                                                    @click="readerLanguage = language">
                                                {{ language.name }}
                                            </button>
                                        </MenuItem>
                                    </MenuItems>
                                </transition>
                            </HeadlessMenu>
                        </div>
                        <div v-if="comic.hasPictureRender"
                             class="flex items-center">
                            <a ref="classic"
                               class="btn-dark font-semibold flex items-center"
                               :class="{'rounded-r-none': comic.pictureRenderLanguages.length}"
                               :href="route('reader_classic', classicLanguage ? [app.locale, comic.id, classicLanguage.code] : [app.locale, comic.id])"
                               target="_blank">
                                <span class="mr-2">
                                    <book-open-icon class="h-5 w-5" />
                                </span>
                                {{ trans['messages.open_classic'] }}
                            </a>
                            <HeadlessMenu v-if="comic.pictureRenderLanguages.length"
                                          as="div"
                                          class="relative">
                                <div>
                                    <MenuButton class="btn-dark bg-slate-600 hover:bg-slate-500 rounded-l-none border-l-0 uppercase flex items-center">
                                        {{ classicLanguage.code }}
                                        <ChevronDownIcon class="h-5 w-5" />
                                    </MenuButton>
                                </div>
                                <transition enter-active-class="transition ease-out duration-100"
                                            enter-from-class="transform opacity-0 scale-95"
                                            enter-to-class="transform opacity-100 scale-100"
                                            leave-active-class="transition ease-in duration-75"
                                            leave-from-class="transform opacity-100 scale-100"
                                            leave-to-class="transform opacity-0 scale-95">
                                    <MenuItems class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-lg bg-slate-800 py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                                        <MenuItem v-for="language in comic.pictureRenderLanguages"
                                                  v-slot="{ active }"
                                                  :key="language.id">
                                            <button :class="[active ? 'bg-slate-700' : '', 'flex items-center w-full px-4 py-2 text-sm text-slate-400']"
                                                    @click="classicLanguage = language">
                                                {{ language.name }}
                                            </button>
                                        </MenuItem>
                                    </MenuItems>
                                </transition>
                            </HeadlessMenu>
                        </div>
                    </template>
                    <a v-else
                       class="py-2 mt-auto">
                        {{ trans['messages.coming_soon'] }}...
                    </a>
                </div>
                <!-- eslint-enable max-len -->
            </div>
        </div>
        <div class="mb-20">
            <comic-row :comics="comics" />
        </div>
    </section>
    <!-- eslint-enable max-len -->
</template>

<script setup>
import {
    ChevronDownIcon
} from '@heroicons/vue/20/solid';

import {
    BookOpenIcon
} from '@heroicons/vue/24/outline';

import {
    Menu as HeadlessMenu,
    MenuButton,
    MenuItem,
    MenuItems
} from '@headlessui/vue';

import { ref, defineProps, defineOptions } from 'vue';
import ComicRow from '../../base/ComicRow';
import Layout from '../../base/Layout';

require('lity');

const { comic } = defineProps({
    app: {
        type: Object,
        required: true
    },

    comic: {
        type: Object,
        required: true
    },

    comics: {
        type: Array,
        required: true
    },

    trans: {
        type: Object,
        required: true
    },

    transNames: {
        type: Object,
        required: true
    }
});

defineOptions({
    layout: Layout
});

const readerLanguage = ref();
const classicLanguage = ref();

if (comic.renderLanguages.length) {
    const [first] = comic.renderLanguages;

    readerLanguage.value = first;
}

if (comic.pictureRenderLanguages.length) {
    const [first] = comic.pictureRenderLanguages;

    classicLanguage.value = first;
}
</script>
